import type { AlignmentMode, Frame, MatchResult } from '~/types/pipeline';

/**
 * Compute the aligned x/y offset for a trimmed region within its source canvas.
 * Modes: none, center, bottom-center, pivot.
 * localOffset = trim position relative to cell origin.
 * pivot = unit coordinates in frame space (0..1) (meaningful only for 'pivot' mode).
 */
function alignedOffset(
  localOffset: { x: number; y: number },
  trimW: number,
  trimH: number,
  sourceW: number,
  sourceH: number,
  mode: AlignmentMode,
  pivot: { x: number; y: number },
): { x: number; y: number } {
  switch (mode) {
    case 'none':
      return { x: localOffset.x, y: localOffset.y };
    case 'center':
      return {
        x: Math.round((sourceW - trimW) / 2),
        y: Math.round((sourceH - trimH) / 2),
      };
    case 'bottom-center':
      return {
        x: Math.round((sourceW - trimW) / 2),
        y: sourceH - trimH,
      };
    case 'pivot': {
      const cx = sourceW / 2;
      const cy = sourceH / 2;
      const dx = cx - pivot.x * sourceW;
      const dy = cy - pivot.y * sourceH;
      return {
        x: Math.round(localOffset.x + dx),
        y: Math.round(localOffset.y + dy),
      };
    }
    case 'reference-point':
      return { x: localOffset.x, y: localOffset.y };
    default:
      return { x: localOffset.x, y: localOffset.y };
  }
}

function indexMatchesByFrameIndex(matches: readonly MatchResult[]): Map<number, MatchResult> {
  const out = new Map<number, MatchResult>();
  for (const match of matches) {
    if (!out.has(match.frameIndex)) out.set(match.frameIndex, match);
  }
  return out;
}

/**
 * Apply alignment to all frames, returning new Frame objects with updated
 * spriteSourceSize.{x, y}. Does not mutate input frames.
 */
export function alignFrames(
  frames: readonly Frame[],
  mode: AlignmentMode,
  opts: { matches?: readonly MatchResult[] } = {},
): Frame[] {
  const matchesByFrameIndex =
    mode === 'reference-point' && opts.matches ? indexMatchesByFrameIndex(opts.matches) : null;

  return frames.map((frame) => {
    const { cellRect, trimRect, sourceSize } = frame;
    const localOffset = {
      x: trimRect.x - cellRect.x,
      y: trimRect.y - cellRect.y,
    };
    // For reference-point we use cell-relative positioning ('none') as the
    // base, then offset by the matched dx/dy. Anything else (e.g. 'center')
    // re-positions the trim per-frame and double-compensates against the
    // per-frame trim shift, which prevents stabilization.
    const baseMode: AlignmentMode = mode === 'reference-point' ? 'none' : mode;
    let offset = alignedOffset(
      localOffset,
      trimRect.w,
      trimRect.h,
      sourceSize.w,
      sourceSize.h,
      baseMode,
      frame.pivot as { x: number; y: number },
    );
    if (mode === 'reference-point' && matchesByFrameIndex) {
      const m = matchesByFrameIndex.get(frame.index);
      if (m && m.confidence >= 0.6) {
        offset = { x: offset.x + m.dx, y: offset.y + m.dy };
      }
    }
    return {
      ...frame,
      spriteSourceSize: {
        x: offset.x,
        y: offset.y,
        w: trimRect.w,
        h: trimRect.h,
      },
    };
  });
}
